/**
 * Opens a PDF blob in a hidden iframe and triggers the browser print dialog.
 */
export const printPdfBlob = async (blob: Blob): Promise<void> => {
  const objectUrl = URL.createObjectURL(blob);

  await new Promise<void>((resolve, reject) => {
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.src = objectUrl;

    let settled = false;

    const cleanup = () => {
      iframe.remove();
      URL.revokeObjectURL(objectUrl);
    };

    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      fn();
    };

    iframe.onload = () => {
      const win = iframe.contentWindow;
      if (!win) {
        cleanup();
        finish(() => reject(new Error('Failed to open PDF for printing.')));
        return;
      }

      const onAfterPrint = () => {
        win.removeEventListener('afterprint', onAfterPrint);
        cleanup();
        finish(() => resolve());
      };
      win.addEventListener('afterprint', onAfterPrint);

      // Fallback if afterprint never fires (some browsers).
      setTimeout(() => {
        cleanup();
        finish(() => resolve());
      }, 60_000);

      try {
        win.focus();
        win.print();
        // Resolve once the dialog is shown; do not wait for afterprint to unblock the UI.
        finish(() => resolve());
      } catch {
        cleanup();
        finish(() => reject(new Error('Failed to print PDF.')));
      }
    };

    iframe.onerror = () => {
      cleanup();
      finish(() => reject(new Error('Failed to load PDF for printing.')));
    };

    document.body.appendChild(iframe);
  });
};
